import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, NotebookPen, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useGroups } from "@/context/GroupContext";
import { useI18n } from "@/context/I18nContext";

export default function RemarksPage() {
  const { activeGroup } = useGroups();
  const { t } = useI18n();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    if (!activeGroup?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get("/remarks");
      setText(data.text || "");
      dirtyRef.current = false;
    } catch (_e) {
      void _e;
      toast.error("Offline — no cached data");
    } finally {
      setLoading(false);
    }
  }, [activeGroup?.id]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (value) => {
    dirtyRef.current = false;
    setSaving(true);
    try {
      await api.put("/remarks", { text: value });
      setSavedAt(new Date());
    } catch (_e) {
      void _e;
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  }, []);

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    dirtyRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => save(v), 1200);
  };

  const onBlur = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (dirtyRef.current) save(text);
  };

  const erase = async () => {
    if (!window.confirm(t("remarks.reset_confirm"))) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setText("");
    await save("");
    toast.success(t("remarks.reset_done"));
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="remarks-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-espresso flex items-center gap-2">
            <NotebookPen className="w-7 h-7 text-olive" />
            {t("remarks.title")}
          </h1>
          <p className="text-sm text-espresso/70 mt-1">
            {activeGroup?.name ? <span className="text-olive font-semibold">{activeGroup.name}</span> : null}
            {activeGroup?.name ? " · " : ""}
            {t("remarks.sub")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-espresso/50" data-testid="remarks-save-status">
            {saving
              ? t("remarks.saving")
              : savedAt
                ? `${t("remarks.saved")} ${savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                : ""}
          </span>
          <button
            type="button"
            onClick={erase}
            disabled={loading || saving || !text}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 transition-colors disabled:opacity-40"
            data-testid="remarks-erase-button"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            {t("remarks.reset")}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center">
          <Loader2 className="w-5 h-5 animate-spin inline-block text-espresso/60" />
        </div>
      ) : (
        <textarea
          value={text}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={t("remarks.placeholder")}
          rows={18}
          className="w-full bg-white border border-app-border rounded-xl px-4 py-3 text-sm text-espresso leading-relaxed outline-none focus:border-olive resize-y shadow-sm"
          data-testid="remarks-textarea"
        />
      )}
    </div>
  );
}
